import type { ContentHref, WebPage, WebPageHref, WebPageKey } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectContentItem,
  selectPathToRootWebConfigurations,
  selectWebPage,
} from '@newStore/documentApi/documentApiSelectors';
import { sanitizeHTML } from '@store/helpers/documentHelpers';
import { replacePathSpecialCharacters } from '@store/helpers/webConfigHelpers';
import uuidv4 from 'uuid/v4';
import { selectWebsiteHrefForRoot } from './nodeTypeConfigSelectors';

const getDefaultPath = (title: string | undefined, parentPath: string | undefined) => {
  return `${parentPath || ''}/${replacePathSpecialCharacters(
    sanitizeHTML(title || '', 'clearAll').toLowerCase(),
  )}`;
};

export const selectOrCreateWebPageConfig = (
  state: RootState,
  contentHref: ContentHref,
): Partial<WebPage> => {
  const webPage = selectWebPage(state, contentHref);
  if (webPage) {
    return webPage;
  }

  const website = selectWebsiteHrefForRoot(state, contentHref);

  if (!website) {
    throw new Error('Website is not set');
  }

  const parentWebConfig = selectPathToRootWebConfigurations(state, contentHref)[1];
  const contentNode = selectContentItem(state, contentHref);
  const path = getDefaultPath(contentNode.title as string, parentWebConfig?.path);

  const key = uuidv4() as WebPageKey;
  const webPagehref = `/web/pages/${key}` as WebPageHref;

  return {
    $$meta: {
      permalink: webPagehref,
      type: 'WEBPAGE',
    },
    key,
    source: {
      href: contentHref,
    },
    website: {
      href: website,
    },
    path,
    externalReferences: [state.documentUI.currentDocument as ContentHref],
  };
};
